import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { PatientQuestionnaire } from "@/types/patientQuestionnaire"; // Import PatientQuestionnaire type
import { PostgrestError } from "@supabase/supabase-js";

export const QuestionnaireService = {
  async submitQuestionnaire(data: Record<string, string | number | boolean>, userId: string): Promise<{ success: boolean, id?: string }> { // Changed Record<string, any> to be more specific
    try {
      // Transform the form data into database format
      // Ensure age is converted to string to match PatientQuestionnaire type if necessary,
      // or adjust PatientQuestionnaire type for age to be number if that's the database schema.
      // For now, assuming age in DB is string as per PatientQuestionnaire type.
      const formattedData: Omit<PatientQuestionnaire, 'id' | 'created_at' | 'updated_at' | 'total_score' | 'risk_level'> & { age: string } = {
        user_id: userId,
        patient_first_name: typeof data.firstName === 'string' ? data.firstName : "",
        patient_last_name: typeof data.lastName === 'string' ? data.lastName : "",
        age: typeof data.age === 'number' ? data.age.toString() : (typeof data.age === 'string' ? data.age : "0"),
        race: typeof data.race === 'string' ? data.race : "",
        family_glaucoma: data.familyGlaucoma === "yes",
        ocular_steroid: data.ocularSteroid === "yes",
        intravitreal_steroids: data.intravitreal === "yes",
        systemic_steroid: data.systemicSteroid === "yes",
        iop_baseline_elevated: data.iopBaseline === "yes",
        vertical_disc_asymmetry: data.verticalAsymmetry === "yes",
        vertical_cd_ratio_elevated: data.verticalRatio === "yes"
      };

      console.log("Submitting questionnaire data:", formattedData);

      // Use the authenticated user's default permissions
      const { data: insertedData, error } = await supabase
        .from('patient_questionnaires')
        .insert([formattedData])
        .select();
      
      if (error) {
        console.error("Error submitting questionnaire:", error);
        throw error;
      }
      
      const id = insertedData?.[0]?.id;
      console.log("Questionnaire submitted successfully, ID:", id);
      
      return { success: true, id };
    } catch (error: unknown) { // Changed any to unknown
      console.error("Error submitting questionnaire:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Error submitting questionnaire: ${errorMessage}`);
      return { success: false };
    }
  },

  async fetchQuestionnaires(userId: string): Promise<PatientQuestionnaire[]> {
    try {
      // Use the authenticated user's default permissions
      const { data, error } = await supabase
        .from('patient_questionnaires')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching questionnaires:", error);
        throw error;
      }
      
      return data || [];
    } catch (error: unknown) { // Changed any to unknown
      console.error("Error fetching questionnaires:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Error fetching questionnaires: ${errorMessage}`);
      return [];
    }
  }
};
